import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import {
  Backdrop,
  ModalContainer,
  CloseButton,
  FormWrapper,
  ModalHeader,
  ModalTitle,
  ModalSubtitle,
  ModalContent,
  ModalFooter,
  PrimaryButton,
  SecondaryButton,
} from './StyledComponents';

// Close icon SVG
const CloseIcon = () => (
  <svg width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
    <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z" />
  </svg>
);

/**
 * FormModal - A reusable modal component for forms
 *
 * @param {boolean} isOpen - Controls modal visibility
 * @param {Function} onClose - Callback when modal is closed
 * @param {string} title - Modal title
 * @param {string} subtitle - Optional subtitle/description
 * @param {ReactNode} children - Form content
 * @param {string} submitText - Text for submit button
 * @param {string} cancelText - Text for cancel button
 * @param {Function} onSubmit - Submit handler
 * @param {boolean} isSubmitting - Loading state for submit button
 * @param {string} maxWidth - Max width of modal (default: 540px)
 */
const FormModal = ({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  submitText = 'Save',
  cancelText = 'Cancel',
  onSubmit,
  isSubmitting = false,
  maxWidth = '540px',
}) => {
  const [modalRoot, setModalRoot] = useState(null);

  // Get modal root on mount
  useEffect(() => {
    setModalRoot(document.getElementById('modal-root') || document.body);
  }, []);

  // Handle escape key press
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && !isSubmitting) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, isSubmitting, onClose]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isSubmitting) {
      onClose();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (onSubmit) {
      onSubmit(e);
    }
  };

  if (!isOpen || !modalRoot) return null;

  return ReactDOM.createPortal(
    <EnhancedThemeProvider>
      <Backdrop onClick={handleBackdropClick}>
        <ModalContainer
          role="dialog"
          aria-labelledby="form-modal-title"
          aria-modal="true"
          maxWidth={maxWidth}
          onClick={(e) => e.stopPropagation()}
        >
          <CloseButton
            onClick={onClose}
            aria-label="Close modal"
            disabled={isSubmitting}
          >
            <CloseIcon />
          </CloseButton>

          <FormWrapper onSubmit={handleSubmit}>
            <ModalHeader>
              <ModalTitle id="form-modal-title">{title}</ModalTitle>
              {subtitle && <ModalSubtitle>{subtitle}</ModalSubtitle>}
            </ModalHeader>

            <ModalContent>{children}</ModalContent>

            <ModalFooter>
              <SecondaryButton
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
              >
                {cancelText}
              </SecondaryButton>
              <PrimaryButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : submitText}
              </PrimaryButton>
            </ModalFooter>
          </FormWrapper>
        </ModalContainer>
      </Backdrop>
    </EnhancedThemeProvider>,
    modalRoot
  );
};

FormModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  children: PropTypes.node.isRequired,
  submitText: PropTypes.string,
  cancelText: PropTypes.string,
  onSubmit: PropTypes.func,
  isSubmitting: PropTypes.bool,
  maxWidth: PropTypes.string,
};

export default FormModal;

// Export DeleteConfirmModal
export { default as DeleteConfirmModal } from './DeleteConfirmModal';

// Export styled components for custom usage
export {
  Backdrop,
  ModalContainer,
  CloseButton,
  FormWrapper,
  ModalHeader,
  ModalTitle,
  ModalSubtitle,
  ModalContent,
  ModalFooter,
  PrimaryButton,
  SecondaryButton,
} from './StyledComponents';

// Export form-specific styled components
export {
  FormGroup,
  FormLabel,
  FormInput,
  FormTextarea,
  ToggleRow,
  ToggleInfo,
  ToggleTitle,
  ToggleDescription,
  ErrorMessage,
  Divider,
  SectionTitle,
} from './StyledComponents';
